import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';

import {
  archiveCompanyFile,
  fetchCompanyFile,
  fetchCompanyFileReferences,
  fetchFileLibraryFiles,
  fetchFileLibraryUsage,
  restoreCompanyFile,
  uploadCompanyFile,
} from './api';
import type {
  CompanyFileDto,
  CompanyFileReferencesDto,
  FileLibraryListResult,
  FileLibraryListParams,
  FileLibraryUsageDto,
  UploadCompanyFileInput,
  UploadCompanyFileResult,
} from './types';

export const FILE_LIBRARY_QUERY_KEY = 'file-library' as const;

export const useFileLibraryUsage = (companyId?: string, enabled = true) =>
  useQuery<FileLibraryUsageDto, ApiClientError>({
    queryKey: [FILE_LIBRARY_QUERY_KEY, 'usage', companyId ?? 'own'],
    queryFn: () => fetchFileLibraryUsage(companyId),
    enabled,
  });

export const useFileLibraryFiles = (params: FileLibraryListParams = {}, enabled = true) =>
  useQuery<FileLibraryListResult, ApiClientError>({
    queryKey: [FILE_LIBRARY_QUERY_KEY, 'files', params],
    queryFn: () => fetchFileLibraryFiles(params),
    enabled,
  });

export const useCompanyFile = (companyFileId?: string | null) =>
  useQuery<CompanyFileDto, ApiClientError>({
    queryKey: [FILE_LIBRARY_QUERY_KEY, 'file', companyFileId],
    queryFn: () => fetchCompanyFile(companyFileId!),
    enabled: !!companyFileId,
  });

export const useCompanyFileReferences = (companyFileId?: string | null) =>
  useQuery<CompanyFileReferencesDto, ApiClientError>({
    queryKey: [FILE_LIBRARY_QUERY_KEY, 'references', companyFileId],
    queryFn: () => fetchCompanyFileReferences(companyFileId!),
    enabled: !!companyFileId,
  });

export const useUploadCompanyFile = () => {
  const qc = useQueryClient();
  return useMutation<UploadCompanyFileResult, ApiClientError, UploadCompanyFileInput>({
    mutationFn: uploadCompanyFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FILE_LIBRARY_QUERY_KEY] });
    },
  });
};

export const useArchiveCompanyFile = () => {
  const qc = useQueryClient();
  return useMutation<CompanyFileDto, ApiClientError, string>({
    mutationFn: archiveCompanyFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FILE_LIBRARY_QUERY_KEY] });
    },
  });
};

export const useRestoreCompanyFile = () => {
  const qc = useQueryClient();
  return useMutation<CompanyFileDto, ApiClientError, string>({
    mutationFn: restoreCompanyFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [FILE_LIBRARY_QUERY_KEY] });
    },
  });
};
